import { BaseAPIRequestFactory, RequiredError } from './baseapi';
import type { Configuration } from '../configuration';
import { RequestContext, HttpMethod, ResponseContext, HttpInfo } from '../http/http';
import { ObjectSerializer } from '../models/ObjectSerializer';
import { ApiException } from './exception';
import { isCodeInRange } from '../util';
import type {SecurityAuthentication} from '../auth/auth';
import type {HttpFile} from '../http/http';


import { ListLogSourcesResponse } from '../models/ListLogSourcesResponse';

/**
 * no description
 */
export class LogSourcesApiRequestFactory extends BaseAPIRequestFactory {

  /**
   * List Log Sources
   * @param projectId ID of the project whose log sources you want to retrieve. You can get your project ID from the [API Credentials](https://console.eon.io/global-settings/api-credentials) page in your global settings. 
   * @param pageToken Cursor that points to the first record of the next page of results. Get this value from the previous response. To preserve the results in the same order, use the same sorting and filters in the first request as all subsequent requests. 
   * @param pageSize Maximum number of items to return in the response.
   * @param locationResourceId Filter log sources by parent resource ID (e.g., S3 bucket resource ID).
   * @param type Filter log sources by type.
   */
  public async listLogSources(projectId: string, pageToken?: string, pageSize?: number, locationResourceId?: string, type?: 'CLOUDTRAIL' | 'DATADOG' | 'CLOUDWATCH' | 'SPLUNK', _options?: Configuration): Promise<RequestContext> {
    let _config;
    if (!_options) {
      _config = this.configuration;
    } else {
      _config = Object.assign({}, _options, this.configuration);
    }
    


    // verify required parameter 'projectId' is not null or undefined
    if (projectId === null || projectId === undefined) {
      throw new RequiredError("LogSourcesApi", "listLogSources", "projectId");
    }






    // Path Params
    const localVarPath = '/projects/{projectId}/log-sources/list'
      .replace('{' + 'projectId' + '}', encodeURIComponent(String(projectId)));

    let signal = _config.timeout ? AbortSignal.timeout(_config.timeout) : undefined;
    // Make Request Context
    const requestContext = _config.baseServer!.makeRequestContext(localVarPath, HttpMethod.POST, signal);
    requestContext.setHeaderParam("Accept", "application/json, */*;q=0.8")

    // Query Params
    if (pageToken !== undefined) {
      requestContext.setQueryParam("pageToken", ObjectSerializer.serialize(pageToken, "string", "tobedefined"));
    }

    // Query Params
    if (pageSize !== undefined) {
      requestContext.setQueryParam("pageSize", ObjectSerializer.serialize(pageSize, "number", ""));
    }

    // Query Params
    if (locationResourceId !== undefined) {
      requestContext.setQueryParam("locationResourceId", ObjectSerializer.serialize(locationResourceId, "string", "uuid"));
    }

    // Query Params
    if (type !== undefined) {
      requestContext.setQueryParam("type", ObjectSerializer.serialize(type, "'CLOUDTRAIL' | 'DATADOG' | 'CLOUDWATCH' | 'SPLUNK'", "tobedefined"));
    }


    let authMethod: SecurityAuthentication | undefined;
    // Apply auth methods
    authMethod = _config.authMethods!["ApiKeyAuth"]
    if (authMethod?.applySecurityAuthentication) {
      await authMethod?.applySecurityAuthentication(requestContext);
    }

    return requestContext;
  }

}

export class LogSourcesApiResponseProcessor {

  /**
   * Unwraps the actual response sent by the server from the response context and deserializes the response content
   * to the expected objects
   *
   * @params response Response returned by the server for a request to listLogSources
   * @throws ApiException if the response code was not in [200, 299]
   */
   public async listLogSourcesWithHttpInfo(response: ResponseContext): Promise<HttpInfo<ListLogSourcesResponse >> {
    if (isCodeInRange("200", response.httpStatusCode)) {
      const body: ListLogSourcesResponse = ObjectSerializer.deserialize(
        await response.body.json(),
        "ListLogSourcesResponse", ""
      ) as ListLogSourcesResponse;
      return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
    }
    if (isCodeInRange("1XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Informational", undefined, response.headers, response);
    }
    if (isCodeInRange("3XX", response.httpStatusCode)) {
      throw new ApiException<undefined>(response.httpStatusCode, "Redirect", undefined, response.headers, response);
    }
    if (isCodeInRange("4XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Client Error", body, response.headers, response);
    }
    if (isCodeInRange("5XX", response.httpStatusCode)) {
      const body: Error = ObjectSerializer.deserialize(
        await response.body.json(),
        "Error", ""
      ) as Error;
      throw new ApiException<Error>(response.httpStatusCode, "Internal Server Error", body, response.headers, response);
    }

    // Work around for missing responses in specification, e.g. for petstore.yaml
    if (response.httpStatusCode < 200 || response.httpStatusCode > 299) {
      throw new ApiException<string | Blob | undefined>(response.httpStatusCode, "Unknown API Status Code!", await response.getBodyAsAny(), response.headers, response);
    }
    const body: ListLogSourcesResponse = await response.body.json() as ListLogSourcesResponse;
    return new HttpInfo(response.httpStatusCode, response.headers, response.body, body);
  }

}
